import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import PhoneMockupLeft from "@/assets/lexi-tips-left-hero-opt.png";
import PhoneMockupRight from "@/assets/lexi-tips-right-hero-opt.png";
import conversationData from "@/data/conversations.json";
import "@/styles/_hero.scss";
import { motion, useScroll, useTransform } from "framer-motion";
import MessageBubble from "@/components/chat";
import { LexiGoogleProofRow } from "@/components/LexiGoogleReviews";
import { Button } from "@/components/ui/button";
import { useIsSignedIn } from "@/hooks/useIsSignedIn";
import StatsSection from "./stats-section";

function HeroSection() {
  const INITIAL_CHAT_DELAY = 2000;
  const [conversation, setConversation] = useState([]);
  const navigate = useNavigate();
  const isSignedIn = useIsSignedIn();
  const sectionRef = useRef(null);

  const { scrollYProgress } = useScroll({
    target: sectionRef,
    offset: ["start end", "end start"],
    layoutEffect: false,
  });

  const yParallax1 = useTransform(scrollYProgress, [0, 1], ["0%", "20%"]);
  const yParallax2 = useTransform(scrollYProgress, [0, 1], ["0%", "-20%"]);

  useEffect(() => {
    // Get the hash from URL (remove the # symbol)
    const hash = window.location.hash.slice(1);
    const variations = conversationData.variations;

    let selectedConversation;

    if (hash) {
      // Try to find a conversation matching the hash
      selectedConversation = variations.find(
        (v) => v.id.toLowerCase() === hash.toLowerCase(),
      );
    }

    // If no hash or no matching conversation found, select random
    if (!selectedConversation) {
      selectedConversation =
        variations[Math.floor(Math.random() * variations.length)];
    }

    setConversation(selectedConversation.messages);
  }, []);

  return (
    <>
      <section id="hero" ref={sectionRef} className="relative min-h-screen">
        <div className="absolute inset-0 bg-white -z-30">
          <div
            className="absolute inset-0 -z-20"
            style={{
              backgroundImage: `radial-gradient(circle at 1px 1px, #e5e5e5 1px, transparent 0)`,
              backgroundSize: "24px 24px",
            }}
          />
          <div
            className="absolute left-1/2 top-[60%] -translate-x-1/2 -translate-y-1/2 w-[800px] h-[800px] -z-10"
            style={{
              background:
                "radial-gradient(50% 50% at 50% 50%, rgba(255, 20, 147, 0.1) 0%, rgba(255, 255, 255, 0) 100%)",
            }}
          />
          <div className="absolute bottom-0 left-0 right-0 h-[300px] bg-gradient-to-t from-black/10 via-transparent to-transparent -z-20" />
        </div>
        <div className="container mx-auto px-4 pt-[6.5rem] md:pt-32 2xl:pt-40 relative z-10">
          <div className="flex flex-col items-center text-center max-w-4xl mx-auto">
            <motion.h1
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.7, delay: 0.2 }}
              className="text-2xl md:text-5xl lg:text-5xl xl:text-5xl 2xl:text-6xl font-bold leading-[1.2em] md:leading-[1.3em] mb-6"
            >
              AI-Powered Horse Racing Tips{" "}
              <span className="italic lg:block">updated right up to the jump</span>
            </motion.h1>

            {/* <AnimateOnScroll> */}
            <StatsSection />
            {/* </AnimateOnScroll> */}

            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.7, delay: 0.4 }}
              className="space-y-4 mb-12"
            >
              <p className="text-gray-600 mb-2">
                <span className="text-2xl md:text-2xl 2xl:text-4xl text-brand-pink font-black uppercase block mb-1 md:mb-3 ">
                  Every race, three times <br />
                </span>
                <span className="text-md md:text-lg 2xl:text-2xl text-black font-black uppercase">
                  Early Mail, Race Day & Late Mail
                </span>
                <br />
              </p>

              <div className="flex flex-wrap items-center justify-center gap-3 mt-4">
                {isSignedIn ? (
                  // Signed in: straight to their tips, never back to /signup
                  <Button
                    variant="outline"
                    className="inline-block px-8 py-3 bg-brand-pink text-white rounded-lg font-semibold hover:bg-[#e391c4] transition-all duration-200"
                    onClick={() => navigate("/todays-tips")}
                    data-gtm-element-id="hero_mytips_button-1"
                  >
                    My tips
                  </Button>
                ) : (
                  <>
                    <Button
                      variant="outline"
                      className="inline-block px-8 py-3 bg-white text-brand-pink border border-brand-pink rounded-lg font-semibold hover:bg-[#FDF2F8] transition-all duration-200"
                      onClick={() => navigate("/todays-tips")}
                      data-gtm-element-id="hero_signin_button-1"
                    >
                      Sign in
                    </Button>
                    <Button
                      variant="outline"
                      className="inline-block px-8 py-3 bg-brand-pink text-white rounded-lg font-semibold hover:bg-[#e391c4] transition-all duration-200"
                      onClick={() => navigate("/signup")}
                      data-gtm-element-id="hero_signup_button-1"
                    >
                      Get tips now!
                    </Button>
                  </>
                )}
              </div>

              {/* Real Google reviews (owner, 2 Nov 2026), signed in or out */}
              <LexiGoogleProofRow className="pt-2" />
            </motion.div>

            <div className="relative mt-0 w-full max-w-[900px] m-0 pointer-events-none">
              <div
                className="chat-container flex flex-col justify-end h-[250px] md:h-[300px] w-full max-w-[550px] mx-auto absolute px-[20px] pb-[20px] z-50 left-1/2 transform -translate-x-1/2"
                style={{
                  animationDelay: `${INITIAL_CHAT_DELAY}ms`,
                  maskImage:
                    "linear-gradient(to bottom, transparent, black 10%, black 90%, transparent)",
                  WebkitMaskImage:
                    "linear-gradient(to bottom, transparent, black 10%, black 90%, transparent)",
                }}
              >
                {conversation.map((msg, index) => (
                  <MessageBubble
                    key={index}
                    userName={msg.userName}
                    message={msg.message}
                    delay={msg.delay}
                    previousMessageDelay={
                      index > 0 ? conversation[index - 1].delay : 0
                    }
                    initialDelay={INITIAL_CHAT_DELAY}
                  />
                ))}
              </div>

              <div className="relative w-full max-w-[900px] h-full min-h-[900px] m-0 z-20 mt-60">
                <motion.div
                  initial={{ opacity: 0, scale: 0.85, rotate: 15 }}
                  animate={{ opacity: 1, scale: 0.95, rotate: 0 }}
                  transition={{ duration: 0.7, delay: 0.8 }}
                  style={{ y: yParallax1 }}
                  className="relative w-full z-20"
                >
                  <img
                    src={PhoneMockupLeft}
                    alt="Lexi App Interface Mockup 1"
                    className="relative w-[80%] lg:w-full max-w-[500px] m-0 z-20 top-[50px] left-[10%] lg:left-[10%]"
                  />
                </motion.div>
                <motion.div
                  initial={{ opacity: 0, scale: 0.85, rotate: -15 }}
                  animate={{ opacity: 1, scale: 0.95, rotate: 0 }}
                  transition={{ duration: 0.7, delay: 0.8 }}
                  style={{ y: yParallax2 }}
                  className="absolute right-0 top-0 w-full z-10"
                >
                  <img
                    src={PhoneMockupRight}
                    alt="Lexi App Interface Mockup 2"
                    className="relative w-full max-w-[470px] mx-0 z-9 ml-auto top-[0%] right-[-10%] lg:right-[0%]"
                  />
                </motion.div>
              </div>
            </div>
          </div>
        </div>
      </section>
    </>
  );
}

export default HeroSection;
